import { createContext, useContext } from 'react'

import Link from '@/components/common/Link'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { SELECTION_UI_ATTRIBUTE } from '@/components/editor-v3/DocxViewer/selectionAnchor'

/**
 * Why the reader may not change the comments at the moment, and where they can
 * instead. Null wherever they may, which is nearly always.
 *
 * Separate from `allowComments`, which is settled as the document is built and
 * re-opens it when it changes. This answers to somebody opening the editor
 * while the reader has the file on screen, so it has to be a live signal — and
 * it holds back changes not yet begun. Whatever the reader has already written
 * stays on screen and stays saveable.
 */
export interface CommentsPaused {
  /** What is in the way, as a sentence. */
  reason: string
  /** Somewhere to leave the comment instead. Absent where there is nowhere: the reason is told on its own. */
  href?: string
}

const CommentsPausedContext = createContext<CommentsPaused | null>(null)

export interface CommentsPausedProviderProps {
  paused: CommentsPaused | null
  children: React.ReactNode
}

/**
 * Over the viewer rather than through it: the controls that read this are the
 * button a selection puts on screen, the list's rows and the margin's cards,
 * which sit three component boundaries apart and share nothing else.
 */
export const CommentsPausedProvider: React.FC<CommentsPausedProviderProps> = ({
  paused,
  children,
}) => (
  <CommentsPausedContext.Provider value={paused}>
    {children}
  </CommentsPausedContext.Provider>
)

export const useCommentsPaused = (): CommentsPaused | null =>
  useContext(CommentsPausedContext)

export interface CommentsPausedTooltipProps {
  paused: CommentsPaused
  children: React.ReactNode
}

/**
 * Why a comment control is doing nothing, with the way out of it underneath
 * where there is one.
 *
 * What it wraps stays a live button — marked `aria-disabled` rather than given
 * `disabled` — because a disabled button fires no pointer events at all, and
 * hovering it is the only way to reach this.
 */
export const CommentsPausedTooltip: React.FC<CommentsPausedTooltipProps> = ({
  paused,
  children,
}) => (
  <TooltipProvider delayDuration={120}>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        noProvider
        // The selection this was offered for has to survive the reader reaching
        // the link, and the tooltip is portalled to the body — outside the
        // button's own marking, and so outside what that marking protects.
        {...{ [SELECTION_UI_ATTRIBUTE]: '' }}
        className="flex max-w-64 flex-col items-start gap-1.5 py-2.5 leading-snug"
      >
        {paused.reason}
        {paused.href ? (
          <Link
            href={paused.href}
            className="whitespace-nowrap text-inherit underline underline-offset-2 dark:text-inherit"
          >
            Open in the editor to comment
          </Link>
        ) : null}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)
